import type { ReactNode } from "react";
import { Calendar } from "lucide-react";

import { cn } from "@/lib/utils";

export interface TimelineRowAction {
  label: string;
  onClick: () => void;
  /** "accent" → olive filled, "primary" → ink-900 filled, "ghost" → outlined. */
  variant?: "accent" | "primary" | "ghost";
  disabled?: boolean;
}

export interface TimelineEvent {
  id: string;
  when: string;
  titleLead: ReactNode;
  titleSuffix?: ReactNode;
  sub: string;
  /** Right-side static value (currency, "—", etc.). Hidden when `actions` are present. */
  amount: string;
  credit?: boolean;
  /** Optional inline actions rendered on the right of the row. */
  actions?: TimelineRowAction[];
}

interface Proximos60DiasProps {
  title: string;
  events: TimelineEvent[];
  ctaLabel?: string;
  onCta?: () => void;
}

export function Proximos60Dias({
  title,
  events,
  ctaLabel,
  onCta,
}: Proximos60DiasProps): JSX.Element {
  return (
    <div className="ef-timeline">
      <div className="ef-timeline-head">
        <div className="title">
          <span className="ico-tile">
            <Calendar className="h-3 w-3" aria-hidden />
          </span>
          {title}
        </div>
        {ctaLabel && onCta ? (
          <a
            className="cta"
            onClick={(e) => {
              e.preventDefault();
              onCta();
            }}
          >
            {ctaLabel} →
          </a>
        ) : null}
      </div>
      <div className="ef-tl-list">
        {events.map((ev) => (
          <div key={ev.id} className="ef-tl-row">
            <div className="when">{ev.when}</div>
            <div className="what">
              <div>
                <b>{ev.titleLead}</b>
                {ev.titleSuffix ? <> {ev.titleSuffix}</> : null}
              </div>
              <div className="sub">{ev.sub}</div>
            </div>
            {ev.actions && ev.actions.length > 0 ? (
              <div className="actions">
                {ev.actions.map((a, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={a.onClick}
                    disabled={a.disabled}
                    className={cn(
                      "fx-btn fx-btn-sm",
                      a.variant === "accent" && "fx-btn-accent",
                      a.variant === "primary" && "fx-btn-primary",
                      (!a.variant || a.variant === "ghost") && "fx-btn-ghost",
                    )}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            ) : (
              <div className={cn("amount", ev.credit && "credit")}>{ev.amount}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export type MiniCalendarKind = "event" | "urgent" | "today";

export interface MiniCalendarProps {
  monthLabel: string;
  yearAccent: string;
  eventCount: number;
  /** ISO weekday (Mon=1..Sun=7) for the 1st of the month. */
  firstDayWeekday: 1 | 2 | 3 | 4 | 5 | 6 | 7;
  daysInMonth: number;
  marks: Record<number, MiniCalendarKind>;
}

const DOW = ["L", "M", "M", "J", "V", "S", "D"];

export function MiniCalendar({
  monthLabel,
  yearAccent,
  eventCount,
  firstDayWeekday,
  daysInMonth,
  marks,
}: MiniCalendarProps): JSX.Element {
  const cells: Array<{ key: string; empty: boolean; n?: number; kind?: MiniCalendarKind }> = [];
  const leadingEmpty = firstDayWeekday - 1;
  for (let i = 0; i < leadingEmpty; i++) {
    cells.push({ key: `e-${i}`, empty: true });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ key: `d-${d}`, empty: false, n: d, kind: marks[d] });
  }
  while (cells.length % 7 !== 0) {
    cells.push({ key: `t-${cells.length}`, empty: true });
  }

  return (
    <div className="ef-mini">
      <div className="ef-mini-head">
        <div className="title">
          {monthLabel} <span className="serif">{yearAccent}</span>
        </div>
        <div className="tag">
          {eventCount} {eventCount === 1 ? "EVENTO" : "EVENTOS"}
        </div>
      </div>
      <div className="ef-mini-grid">
        {DOW.map((d, i) => (
          <div key={`h-${i}`} className="dow">
            {d}
          </div>
        ))}
        {cells.map((c) => (
          <div
            key={c.key}
            className={cn(
              "d",
              c.empty && "empty",
              c.kind === "event" && "event",
              c.kind === "urgent" && "urgent",
              c.kind === "today" && "today"
            )}
          >
            {c.n ?? ""}
          </div>
        ))}
      </div>
      <div className="ef-mini-legend">
        <div className="item">
          <span className="sw today" />
          Hoy
        </div>
        <div className="item">
          <span className="sw urgent" />
          Urgente
        </div>
        <div className="item">
          <span className="sw event" />
          Otros
        </div>
      </div>
    </div>
  );
}
